import ScrollModal from "@/components/common/scroll-modal";
import {
  Button,
  Heading,
  Icon,
  TextField,
  useToast,
} from "@elaniintc/goes-design-system";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateInput } from "@/components/common/DateInput";
import { FileInput } from "@/components/common/input-file";
import {
  ResolutionFormSchema,
  type ResolutionFormData,
} from "@/schemas/digital-files/digital-file";
import { useSaveNewResolution } from "@/queries/digital-files/registries.api";
import { useState } from "react";
import { uploadFile } from "@/queries/files/file.api";
import { getCodeError } from "../../utils";

interface ResolutionModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

export const ResolutionModal = ({ isOpen, onClose }: ResolutionModalProps) => {
  const { mutateAsync: saveNewResolution } = useSaveNewResolution();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ResolutionFormData>({
    resolver: zodResolver(ResolutionFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      code: "",
      description: "",
      resolution_date: "",
      files: [] as File[],
    },
  });

  const handleFormSubmit = async (data: ResolutionFormData) => {
    try {
      setIsLoading(true);
      const { files, ...resolutionData } = data;

      // Paso 1: Crear la resolución
      const result = await saveNewResolution(resolutionData);
      if (!result) {
        throw new Error("Error al crear la resolución");
      }

      // Paso 2: Subir archivos si existen
      if (files && files.length > 0) {
        try {
          await Promise.all(
            files.map((file) =>
              uploadFile({
                file,
                entity_id: result.data.id,
                entity_type: "resolution",
              }),
            ),
          );
        } catch (uploadErr) {
          const errorMessage =
            uploadErr instanceof Error
              ? uploadErr.message
              : "Error al subir archivos";
          console.error("Error uploading files:", errorMessage);
          showToast({
            title: "Resolución guardada",
            description:
              "La resolución se guardó correctamente pero hubo un error al subir algunos archivos",
            options: { type: "warning" },
          });
          setIsLoading(false);
          reset();
          onClose?.();
          return;
        }
      }
      // Éxito completo
      showToast({
        title: "Resolución Guardada",
        description: "La resolución se ha guardado correctamente.",
        options: { type: "success" },
      });
      setIsLoading(false);
      handleClose();
    } catch (error) {
      const errorMessage = getCodeError(error);
      showToast({
        title: "Resolución no guardada",
        description:
          errorMessage ??
          "Hubo un error al guardar la resolución. Por favor, inténtelo de nuevo.",
        options: { type: "error" },
      });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[340px] md:max-w-[520px] max-h-[680px] flex flex-col gap-8"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Agregar Resolución</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={handleClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="w-full flex items-center justify-end gap-3 py-4 px-8">
          <Button
            variant="quiet"
            aria-label="Cerrar"
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            aria-label="Guardar"
            onClick={handleSubmit(handleFormSubmit)}
            disabled={!isValid || isLoading}
          >
            {isLoading && (
              <Icon type="far" name="spinner-third" className="animate-spin" />
            )}
            Guardar
          </Button>
        </div>
      }
    >
      <form className="px-8 flex flex-col gap-4">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              label="Nombre de resolución"
              required
              placeholder="Nombre de resolución"
              error={errors.name?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <TextField
              label="ID de solicitud"
              required
              placeholder="PRO-0000-00-00-000"
              error={errors.code?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              label="Nombre del trámite"
              required
              placeholder="Nombre del trámite"
              error={errors.description?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="resolution_date"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="resolutionDate"
                className="text-body-loud text-size-body-sm font-semibold"
              >
                Fecha de resolución <span className="text-danger">*</span>
              </label>
              <DateInput
                id="resolutionDate"
                captureCurrentTime={true}
                placeholder="DD/MM/AAAA"
                value={field.value}
                onChange={field.onChange}
                maxDate={new Date()}
              />
              {errors.resolution_date && (
                <span className="text-danger text-size-body-xs">
                  {errors.resolution_date.message}
                </span>
              )}
            </div>
          )}
        />

        <Controller
          name="files"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="resolutionFiles"
                className="text-body-loud text-size-body-sm font-semibold"
              >
                Archivos de resolución<span className="text-danger">*</span>
              </label>
              <FileInput
                value={value}
                onChange={onChange}
                acceptedMimeTypes={[
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                ]}
                allowed_mimes={["pdf", "jpg"]}
                allowMultiples={true}
                maxSizeBytes={500 * 1024 * 1024} // 500 MB
              />
              {errors.files && (
                <span className="text-danger text-size-body-xs">
                  {errors.files.message as string}
                </span>
              )}
            </div>
          )}
        />
      </form>
    </ScrollModal>
  );
};
